import { service } from '@ember/service'
import Route from '@ember/routing/route'
import { action } from '@ember/object'
import { assert } from '@ember/debug'
import { macroCondition, isDevelopingApp, isTesting, importSync } from '@embroider/macros'
import { isAbortError, isUnauthorizedError, isForbiddenError } from 'teacher-ui-client/errors/http-error'
import { DEFAULT_LANGUAGE, setIntlLanguage } from 'teacher-ui-client/utils/locales'
import type SchoolRolloverService from 'teacher-ui-client/services/school-rollover'
import type SessionService from 'teacher-ui-client/services/session'
import type { IntlService } from 'ember-intl'
import type AuthToken from '@blakeelearning/auth/services/auth-token'
import type { Log } from '@blakeelearning/log'
import type Store from '@ember-data/store'
import type Transition from '@ember/routing/transition'
import type { DecodedAuthToken } from 'teacher-ui-client/services/session'
import type Teacher from 'teacher-ui-client/models/teacher'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'
import type ReleaseCheckerService from '@blakeelearning/app-refresher/release-checker/service'
import type LongSessionKillerService from '@blakeelearning/app-refresher/long-session-killer/service'
import type SessionTrackerService from '@blakeelearning/app-refresher/session-tracker/service'
import type RefresherService from '@blakeelearning/app-refresher/refresher/service'
import type SubscriptionUtilisationService from 'teacher-ui-client/services/subscription-utilisation'
import type Features from 'teacher-ui-client/services/features'
import config from 'teacher-ui-client/config/environment'
import { join } from 'teacher-ui-client/utils/uri'
import type AnalyticsService from 'teacher-ui-client/services/analytics'
import type School from 'teacher-ui-client/models/school'
import type SchoolStatistics from 'teacher-ui-client/models/school-statistic'
import type RouterService from '@ember/routing/router-service'
import type WindowService from 'teacher-ui-client/services/window'
import type TeacherEvents from 'teacher-ui-client/services/teacher-events'
import type { RouteWithKnownAttributes } from 'teacher-ui-client/services/teacher-events'

async function bootMirage() {
  // don't start mirage if jupiter is running
  if (config.isJupiter) return

  const startMirage: any = importSync('teacher-ui-client/mirage/servers/default')
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
  await startMirage.default({})
}

export default class ApplicationRoute extends Route {
  @service analytics!: AnalyticsService
  @service authToken!: AuthToken
  @service features!: Features
  @service gradeSets!: GradeSetsService
  @service intl!: IntlService
  @service log!: Log
  @service longSessionKiller!: LongSessionKillerService
  @service refresher!: RefresherService
  @service releaseChecker!: ReleaseCheckerService
  @service router!: RouterService
  @service schoolRollover!: SchoolRolloverService
  @service session!: SessionService
  @service sessionTracker!: SessionTrackerService
  @service store!: Store
  @service studentProgress!: StudentProgressService
  @service subscriptionUtilisation!: SubscriptionUtilisationService
  @service teacherEvents!: TeacherEvents
  @service window!: WindowService

  refreshTokenTimer?: NodeJS.Timeout

  constructor(...args: never[]) {
    super(...args)

    this.analytics.setup()

    this.router.on('routeDidChange', this.checkAppRefresher)

    this.router.on('routeDidChange', ({ to }) => {
      this.teacherEvents.track(to as RouteWithKnownAttributes)
    })
  }

  override willDestroy() {
    this.router.off('routeDidChange', this.checkAppRefresher)
  }

  async beforeModel(transition: Transition) {
    // embroider macros should ensure that mirage code is tree-shaken out of production build
    if (macroCondition(isDevelopingApp() && !isTesting())) {
      await bootMirage()
    }

    this.addCaperFeatures()

    /* Set a temporary default language here, so that if an error occurs we can still show error messaging from
     * translations. For example, the application error substate page uses intl translation keys.
     * The language is set again in the afterModel once we have the teacher model and know the selected language to use.
     */
    await setIntlLanguage(this.intl, DEFAULT_LANGUAGE)

    return this.getTokenOrRedirect().match(
      (_token) => {
        this.releaseChecker.start()
        this.longSessionKiller.start()
        const { decodedToken } = this.authToken
        this.session.setAuthUser(decodedToken as unknown as DecodedAuthToken)
        this.studentProgress.baseUrl = config.studentProgressV3Url
        this.studentProgress.baseWritingLegendsUrl = join(config.gravityV3Url, 'teacher-ui/progression/api-get').href
        this.studentProgress.setupRequestInit = () => {
          return this.authToken.token ? { headers: { Authorization: this.authToken.token } } : {}
        }
      },
      (_error) => {
        // Abort the transition to ensure no further model hooks take place
        void transition.abort()
      },
    )
  }

  /**
   * @returns {Teacher} Teacher Ember Data model object.
   */
  async model() {
    const { session, store, schoolRollover, analytics } = this
    const { authUser } = session
    assert('authUser is not set', authUser)

    const schoolIncludes = [
      'district',
      'district.district-coordinators',
      'accent',
      'standards-set',
      'school-subscriptions',
      'subscription-coordinators',
      'subscription-coordinators.teacher',
      'school-partners',
      'school-partners.partner',
    ]
    const teacherIncludes = ['school', 'school-classes', 'teacher-subscriptions', 'dashboard-preferences']

    // Use authUser to fetch the current teacher & school records.
    // From there, use those school/teacher records, rather than authUser.
    const [school, teacher] = await Promise.all([
      /* reload: true on each, to ensure they wait until request fully completes even if the record was included in the
       * other payload. This is to ensure we don't proceed until we have all the includes.
       */
      store.findRecord<School>('school', String(authUser.schoolId), {
        // @ts-expect-error - include types not correct
        include: schoolIncludes.join(','),
        reload: true,
      }),
      store.findRecord<Teacher>('teacher', String(authUser.id), {
        // @ts-expect-error - include types not correct
        include: teacherIncludes.join(','),
        reload: true,
      }),
    ])
    session.setSchool(school)
    session.setTeacher(teacher)

    assert('school id must not be null', school.id)

    analytics.register({ is_homeschool: school.isHomeschool })

    const [subscriptionUtilisations, schoolStatistics] = await Promise.all([
      this.subscriptionUtilisation.fetchAll(),
      store.findRecord<SchoolStatistics>('school-statistic', school.id, { reload: true }),
      schoolRollover.fetchRollovers(),
    ])

    const { schoolSubscriptions } = school
    const { teacherSubscriptions } = teacher

    return {
      teacher,
      subscriptionUtilisations,
      schoolSubscriptions,
      teacherSubscriptions,
      schoolStatistics,
    }
  }

  async afterModel({ teacher }: { teacher: Teacher }) {
    this.teacherEvents.setupTeacherTotter()

    await setIntlLanguage(this.intl, teacher.language)

    assert('teacher id must not be null', teacher.id)

    this.sessionTracker.start(teacher.id)

    await this.gradeSets.loadGradeSets()

    this.scheduleRefresh()
  }

  scheduleRefresh() {
    if (!config.autoRefreshJWT.enabled) return

    const expiry = this.authToken.tokenExpiry

    const oneMinute = 60 * 1000

    const refreshAt = expiry - oneMinute - Date.now() // Refresh the JWT a minute before it expires

    this.refreshTokenTimer = setTimeout(() => {
      /* In case the browser went to sleep or something, do a final check to ensure we're within the expiry window
       * before attempting to refresh the token.
       */
      if (Date.now() < expiry) {
        void this.refreshToken()
      } else {
        this.redirectToLogin({ returnToCurrentLocation: true })
      }
    }, refreshAt)
  }

  async refreshToken() {
    await this.authToken
      .requestToken()
      .andThen((_token) => this.getTokenOrRedirect())
      .map((_token) => this.scheduleRefresh())
  }

  /**
   * Retreives the token. Redirects to login if this fails.
   * Instructs the redirection to avoid a return_to link if a token was found but usertype is wrong, the login page will
   * take them to the correct place.
   */
  getTokenOrRedirect() {
    return this.authToken.getTokenForTeacher().mapErr((err) => {
      const incorrectUserType = err.message.includes('does not match teacher')
      this.redirectToLogin({ returnToCurrentLocation: !incorrectUserType })
      return err
    })
  }

  /**
   * Send them back to the login page so they can get auth'd. If desired, set the return_to, so that when logged in,
   * they'll be back at the page they wanted.
   */
  redirectToLogin = ({ returnToCurrentLocation = false }: { returnToCurrentLocation: boolean }) => {
    const url = new URL(config.loginUrl)
    if (returnToCurrentLocation) url.searchParams.append('return_to', this.window.getWindow.location.href)
    this.window.getWindow.location.assign(url.href)
  }

  checkAppRefresher = () => {
    this.refresher.refreshIfScheduled()
  }

  deactivate() {
    if (this.refreshTokenTimer) clearTimeout(this.refreshTokenTimer)
  }

  // Route error handlers are called synchronously so cannot return a promise
  // which also means we can't use async/await
  @action // @action needed for hook to be called.
  error(error: unknown, _transition: Transition) {
    // don't report abort errors
    if (isAbortError(error)) {
      return false
    }

    // if unauthorized or forbidden, their token would have expired - send them to login and dont report.
    if (isUnauthorizedError(error) || isForbiddenError(error)) {
      this.redirectToLogin({ returnToCurrentLocation: false })
      return false
    }

    if (error instanceof Response) {
      void error.text().then((text) => this.log.error('unknown error occurred', text))
    } else if (error) {
      this.log.error('unknown error occurred', error)
    } else {
      this.log.error('unknown error occurred')
    }

    return true
  }

  /**
   * Caper knows to look for this global variable when loading an activity or
   * variable and use it to control its self-resizing behaviour.
   */
  private addCaperFeatures() {
    window.CAPER_FEATURES = this.features.enabledFlags
  }
}
